import PropTypes from 'prop-types';

import { getKeyPitchClass, getKeyRootDistance } from '../../../../utils/keyDistance';
import { spellKey } from '../../../../utils/partKey';

import './SongTransition.scss';

/**
 * One key moving to another, wherever that happens: between two songs in continuous view, at the
 * end of a song in single view, and at the part where a song changes key mid-song.
 *
 * One pill, not three markers — it is the same fact being reported, and a reader who has learnt to
 * read it between songs shouldn't have to learn it again inside a part.
 *
 * `viaKey` is for a song that doesn't end where it started: one in Dm whose last chorus lifted to
 * Em hands the band over from Em, so the row reads Dm › Em › G and the interval is measured across
 * the final arrow. Measuring it from the stored key instead would name a move nobody plays.
 *
 * The root is a span rather than a div so the mid-song use is valid inside ViewSongPart, which is
 * itself a span. `display: flex` comes from the stylesheet either way, so nothing about how the
 * two existing uses lay out changes.
 */
const SongTransition = ({ fromKey, toKey, viaKey }) => {
  // Where the move actually starts. Without a viaKey that is the song's own key, so everything
  // below reads the same as it always did for the two-key case.
  const startKey = viaKey || fromKey;
  // A key that can't be read has no distance to report, and saying so beats inventing one.
  // getKeyRootDistance answers 0 for an unreadable key, which is why this is asked separately
  // rather than inferred from the distance it returns.
  const isReadable = getKeyPitchClass(startKey) !== null && getKeyPitchClass(toKey) !== null;
  const distance = isReadable ? getKeyRootDistance(startKey, toKey) : null;

  // No badge when the roots sit in the same place. That covers the two keys being the same, and
  // also a move that keeps the tonic and changes only the mode — Dm to D, the parallel major,
  // which is nought semitones and still a real change of key. The two names either side of the
  // arrow already report it; a badge reading "0" would only argue with them.
  const showDistance = !isReadable || distance !== 0;

  // With a viaKey the song's own key is history — the band isn't in it when the move happens, and
  // the badge isn't measured from it. It stays on the row for reference, faded so the eye lands on
  // the two keys the move is actually between.
  const fromClass = viaKey ? ' superseded' : '';

  return (
    <span className="SongTransition">
      <span className="content">
        {/* Every key goes through the same speller, or one end of the row says A# and the other Bb */}
        <span className={`from-key${fromClass}`}>{spellKey(fromKey)}</span>
        <span className={`arrow${fromClass}`} aria-hidden="true">
          ›
        </span>
        {viaKey && (
          <>
            {/* Where the song was left, styled as a key being arrived at — because that is what it
                was, for the part that took it there */}
            <span className="to-key">{spellKey(viaKey)}</span>
            <span className="arrow" aria-hidden="true">
              ›
            </span>
          </>
        )}
        <span className="to-key">{spellKey(toKey)}</span>
        {showDistance && <span className="steps">{isReadable ? `${distance > 0 ? '+' : ''}${distance}` : '...'}</span>}
      </span>
    </span>
  );
};

SongTransition.propTypes = {
  /** The key being left */
  fromKey: PropTypes.string.isRequired,
  /** The key being arrived at */
  toKey: PropTypes.string.isRequired,
  /** Where the song was left, when a part took it somewhere other than its own key. The interval
      is measured from here rather than from fromKey */
  viaKey: PropTypes.string,
};

export default SongTransition;
